import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  formatMs,
  formatPct,
  formatUsd,
  type ProviderView,
  type RequestRow,
  type RoutingDecisionResponse,
} from "../lib/api";
import { useSystem } from "../lib/system";
import { healthDisplay } from "../lib/provider-display";
import {
  Dot,
  Metric,
  PageHeader,
  ProviderMark,
  SectionHeader,
  SkeletonRows,
  ErrorState,
  StatusBadge,
  toneForStatus,
} from "../components/ui";

export default function OverviewPage() {
  const { overview: data, loading, error, refresh } = useSystem();
  const [providers, setProviders] = useState<ProviderView[]>([]);
  const [decision, setDecision] = useState<RoutingDecisionResponse | null>(null);

  // Branding needs full provider views — loaded once (not polled).
  useEffect(() => {
    void api<{ data: ProviderView[] }>("/api/providers")
      .then((r) => setProviders(r.data))
      .catch(() => {});
    void api<RoutingDecisionResponse>("/api/router/simulate", {
      method: "POST",
      body: JSON.stringify({
        model: "free-auto",
        messages: [{ role: "user", content: "ping" }],
      }),
    })
      .then(setDecision)
      .catch(() => setDecision(null));
  }, []);

  if (loading && !data) {
    return (
      <div className="mx-auto max-w-6xl">
        <PageHeader title="Overview" subtitle="Free-first routing, provider health, and live traffic." />
        <SkeletonRows rows={6} height={84} />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="mx-auto max-w-6xl">
        <PageHeader title="Overview" subtitle="Free-first routing, provider health, and live traffic." />
        <ErrorState message={error ?? "Failed to load overview"} onRetry={refresh} />
      </div>
    );
  }

  const m = data.metrics;
  const requests = m.requests ?? 0;
  const freeReq = m.freeRequestCount ?? 0;
  const paidCount = Math.max(0, requests - freeReq);
  const fallbackRate = requests > 0 ? (m.fallbacks ?? 0) / requests : 0;
  const isDemoType = (typeId: string) => typeId.startsWith("mock-");

  // Production-facing surfaces exclude demo providers; historical demo
  // requests stay visible in activity but are badge-marked.
  const prodProviders = data.providers.filter((p) => !isDemoType(p.providerTypeId));
  const prodFreeModels = prodProviders.reduce((s, p) => s + (p.freeModelCount ?? 0), 0);
  const unhealthy = prodProviders.filter(
    (p) => p.health === "unhealthy" || p.health === "degraded",
  );
  const operational = unhealthy.length === 0;
  const freeProviders = prodProviders
    .filter((p) => (p.freeModelCount ?? 0) > 0)
    .sort((a, b) => b.freeModelCount - a.freeModelCount);

  const brandingFor = (providerId: string, typeId: string) => {
    const v = providers.find((p) => p.id === providerId || p.providerTypeId === typeId);
    return v?.definition.branding ?? { letter: typeId.slice(0, 2).toUpperCase(), accent: "#3DDC97" };
  };
  const providerViewFor = (providerId: string | null) =>
    providerId ? providers.find((p) => p.id === providerId) : undefined;

  // Prefer explanation.selected; fall back to the top-level decision fields
  // so the card renders whenever a route exists.
  const sel = decision?.explanation?.selected ?? null;
  const preferred = sel
    ? {
        provider: sel.providerDisplayName,
        model: sel.displayName,
        reasons: sel.reasons ?? [],
      }
    : decision?.providerId
      ? {
          provider:
            providerViewFor(decision.providerId)?.name ??
            decision.providerDisplayName ??
            "Unknown provider",
          model: decision.displayName ?? decision.providerModelId,
          reasons: [] as string[],
        }
      : null;
  const chain = decision?.explanation?.fallbackChain ?? decision?.fallbackChain ?? [];
  const fallbacks = chain
    .filter((f) => f.role !== "primary")
    .map((f) => {
      const v = providerViewFor(f.providerId);
      return {
        provider: v?.name ?? "Unknown provider",
        demo: v ? isDemoType(v.providerTypeId) : false,
      };
    })
    .filter((x, i, a) => a.findIndex((y) => y.provider === x.provider) === i)
    .slice(0, 3);
  const prodQuotas = data.quotas.filter((q) => {
    const pv = providers.find((p) => p.id === q.providerId);
    // Demo quota snapshots are kept out of the production-facing card.
    return !(pv && isDemoType(pv.providerTypeId));
  });
  const circuitLabels = [
    ...new Set(data.circuits.map((c) => c.label ?? "Unavailable provider")),
  ];
  const circuitLines = circuitLabels.map(
    (name) => `${name} ${data.circuits.find((c) => (c.label ?? "Unavailable provider") === name)?.state ?? ""}`.trim(),
  );

  return (
    <div className="mx-auto max-w-6xl pb-16">
      <PageHeader
        title="Overview"
        subtitle="One OpenAI-compatible API for your entire inference pool — routed by NEXUS."
        right={
          <span className="text-xs text-canvas-muted">Window: {data.window}</span>
        }
      />

      {/* ── Status banner ─────────────────────────────────────── */}
      <div
        className={`panel mb-5 flex flex-wrap items-center justify-between gap-3 border-l-2 px-5 py-4 ${
          operational ? "border-l-accent" : "border-l-warn"
        }`}
      >
        <div className="flex items-center gap-3">
          <Dot tone={operational ? "ok" : "warn"} pulse />
          <div>
            <div className="text-sm font-semibold">
              {operational ? "NEXUS operational" : "NEXUS degraded"}
            </div>
            <div className="text-xs text-canvas-muted">
              {prodFreeModels} free models · {freeProviders.length} active sources
              {unhealthy.length > 0 && ` · ${unhealthy.length} provider${unhealthy.length === 1 ? "" : "s"} need attention`}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/keys" className="btn-primary !py-1.5 text-xs">Connect your app →</Link>
          <Link to="/providers" className="btn-ghost text-xs">Manage providers</Link>
          <Link to="/router" className="btn-ghost text-xs">Open routing lab</Link>
        </div>
      </div>

      {/* ── Key metrics ───────────────────────────────────────── */}
      <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Metric label="Requests" value={requests} hint={`${m.cacheHits ?? 0} cache hits`} />
        <Metric
          label="Success rate"
          value={formatPct(m.successRate ?? 0)}
          tone={
            requests === 0
              ? undefined
              : (m.successRate ?? 0) >= 0.95
                ? "ok"
                : (m.successRate ?? 0) >= 0.8
                  ? "warn"
                  : "danger"
          }
        />
        <Metric
          label="Fallback rate"
          value={formatPct(fallbackRate)}
          hint={`${m.fallbacks ?? 0} fallbacks`}
          tone={fallbackRate > 0.2 ? "warn" : undefined}
        />
        <Metric label="Avg latency" value={formatMs(m.avgLatencyMs ?? 0)} />
        <Metric label="Avg TTFT" value={formatMs(m.avgTtftMs ?? 0)} />
        <Metric label="Free usage" value={freeReq} tone="ok" hint="requests on free tier" />
        <Metric label="Paid usage" value={paidCount} hint="requests on paid keys" />
        <Metric label="Estimated cost" value={formatUsd(m.totalCostUsd ?? 0)} />
      </div>

      {/* ── Free pool + routing ───────────────────────────────── */}
      <div className="mb-6 grid gap-3 lg:grid-cols-2">
        <section className="panel p-4">
          <SectionHeader
            title="Free Inference Pool"
            right={
              <Link to="/models?filter=free" className="text-xs text-accent hover:underline">
                View free models →
              </Link>
            }
          />
          {freeProviders.length === 0 ? (
            <p className="py-4 text-sm text-canvas-muted">
              No free capacity yet — connect a free or no-key provider.
            </p>
          ) : (
            <ul className="divide-y divide-canvas-border">
              {freeProviders.slice(0, 6).map((p) => {
                const b = brandingFor(p.id, p.providerTypeId);
                const h = healthDisplay(p.health === "healthy" ? "healthy" : p.health === "unhealthy" ? "unhealthy" : "connected");
                return (
                  <li key={p.id} className="flex items-center gap-3 py-2">
                    <ProviderMark letter={b.letter} accent={b.accent} size={28} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        {p.displayName}
                        {isDemoType(p.providerTypeId) && (
                          <span className="chip-muted !text-[10px]">DEMO</span>
                        )}
                      </div>
                      <div className="text-[11px] text-canvas-muted">
                        {formatMs(p.p50LatencyMs)} p50 · {formatPct(p.successRate)} success
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-accent">{p.freeModelCount} free</div>
                      <div className="text-[11px] text-canvas-muted">{p.modelCount} total</div>
                    </div>
                    <Dot tone={h.tone} />
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="panel p-4">
          <SectionHeader
            title="NEXUS Routing"
            subtitle="What free-auto would select right now"
            right={
              <Link to="/router" className="text-xs text-accent hover:underline">
                Routing lab →
              </Link>
            }
          />
          {preferred ? (
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-canvas-muted">Preferred</div>
                <div className="mt-0.5 text-sm font-medium">
                  {preferred.provider}
                  <span className="text-canvas-muted"> / </span>
                  {preferred.model}
                </div>
              </div>
              {fallbacks.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-canvas-muted">Fallback chain</div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                    {fallbacks.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1">
                        {i > 0 && <span className="text-canvas-muted">→</span>}
                        <span className={f.demo ? "text-canvas-muted" : ""}>
                          {f.provider}
                          {f.demo && <span className="chip-muted ml-1 !text-[10px]">demo</span>}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {preferred.reasons.length > 0 && (
                <ul className="space-y-1 text-xs text-canvas-muted">
                  {preferred.reasons.slice(0, 3).map((r) => (
                    <li key={r} className="flex gap-1.5">
                      <span className="text-accent">✓</span>
                      {r}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <p className="py-4 text-sm text-canvas-muted">
              No routable free capacity — connect a provider or check routing lab.
            </p>
          )}
          {circuitLines.length > 0 && (
            <div className="mt-3 border-t border-canvas-border pt-3 text-[11px] text-canvas-muted">
              Circuits: {circuitLines.slice(0, 3).join(" · ")}
              {circuitLines.length > 3 && ` · +${circuitLines.length - 3} more`}
            </div>
          )}
        </section>
      </div>

      {/* ── Provider health ───────────────────────────────────── */}
      <div className="mb-6 grid gap-3 lg:grid-cols-2">
        <section className="panel">
          <div className="panel-header">
            <h2 className="text-sm font-semibold">Provider health</h2>
            <Link to="/providers" className="text-xs text-accent hover:underline">
              Manage →
            </Link>
          </div>
          <div className="divide-y divide-canvas-border">
            {prodProviders.length === 0 && (
              <p className="px-4 py-6 text-sm text-canvas-muted">
                No production providers connected yet.
              </p>
            )}
            {prodProviders.map((p) => {
              const b = brandingFor(p.id, p.providerTypeId);
              return (
                <Link
                  key={p.id}
                  to={`/providers?id=${p.providerTypeId}`}
                  className="flex items-center justify-between px-4 py-2.5 transition hover:bg-canvas-raised"
                >
                  <div className="flex items-center gap-3">
                    <ProviderMark letter={b.letter} accent={b.accent} size={26} />
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium">
                        {p.displayName}
                        {isDemoType(p.providerTypeId) && (
                          <span className="chip-muted !text-[10px]">DEMO</span>
                        )}
                      </div>
                      <div className="text-[11px] text-canvas-muted">
                        {p.modelCount} models · {p.freeModelCount} free · {formatMs(p.p50LatencyMs)}
                      </div>
                    </div>
                  </div>
                  <StatusBadge label={p.health} tone={toneForStatus(p.health)} />
                </Link>
              );
            })}
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <h2 className="text-sm font-semibold">Quota health</h2>
            <span className="chip-muted">{prodQuotas.length} snapshots</span>
          </div>
          <div className="divide-y divide-canvas-border">
            {prodQuotas.length === 0 && (
              <p className="px-4 py-6 text-sm text-canvas-muted">
                No quota snapshots yet — limits appear after verification.
              </p>
            )}
            {prodQuotas.map((q, i) => {
              const limit = q.limits[0];
              const pv = providers.find((p) => p.id === q.providerId);
              if (!limit) {
                return (
                  <div key={`${q.providerId}-${i}`} className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {pv?.name ?? "Unknown provider"}
                      </span>
                      <span className="text-xs text-canvas-muted">
                        No limits reported — provider did not return quota data
                      </span>
                    </div>
                  </div>
                );
              }
              const ratio = limit.limit > 0 ? limit.remaining / limit.limit : 0;
              return (
                <div key={`${q.providerId}-${i}`} className="px-4 py-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {pv?.name ?? "Unknown provider"}
                    </span>
                    <span className="chip-muted">{limit.kind} · {limit.window}</span>
                  </div>
                  <div className="mb-1 flex justify-between text-xs text-canvas-muted">
                    <span>{(limit.remaining ?? 0).toLocaleString()} remaining</span>
                    <span className="mono">of {(limit.limit ?? 0).toLocaleString()}</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-canvas">
                    <div
                      className={`h-full rounded-full ${ratio > 0.3 ? "bg-accent" : ratio > 0.1 ? "bg-warn" : "bg-danger"}`}
                      style={{ width: `${Math.max(2, Math.round(ratio * 100))}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* ── Recent activity ───────────────────────────────────── */}
      <section className="panel">
        <div className="panel-header">
          <h2 className="text-sm font-semibold">Recent activity</h2>
          <Link to="/requests" className="text-xs text-accent hover:underline">
            All requests →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-canvas-border">
                <th className="table-th">Time</th>
                <th className="table-th">Model</th>
                <th className="table-th">Status</th>
                <th className="table-th">Latency</th>
                <th className="table-th">Tokens</th>
                <th className="table-th">Cost</th>
                <th className="table-th">Route</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-canvas-border">
              {data.recentRequests.length === 0 && (
                <tr>
                  <td colSpan={7} className="table-td text-canvas-muted">
                    No requests yet. Send traffic to the gateway to populate this table.
                  </td>
                </tr>
              )}
              {data.recentRequests.map((r) => (
                <ActivityRow key={r.id} r={r} provider={providerViewFor(r.resolvedProviderId)} />
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function ActivityRow({
  r,
  provider,
}: {
  r: RequestRow;
  provider?: ProviderView;
}) {
  const resolvedName = r.routingExplanation?.selected?.displayName;
  const demo = provider?.providerTypeId.startsWith("mock-") ?? false;
  return (
    <tr className="transition hover:bg-canvas-raised">
      <td className="table-td mono text-canvas-muted">
        {new Date(r.createdAt).toLocaleTimeString()}
      </td>
      <td className="table-td">
        <div className="flex items-center gap-1.5 font-medium">
          {r.requestedModel}
          {demo && <span className="chip-muted !text-[10px]">DEMO</span>}
        </div>
        {resolvedName && resolvedName !== r.requestedModel && (
          <div className="text-[11px] text-canvas-muted">→ {resolvedName}</div>
        )}
      </td>
      <td className="table-td">
        <StatusBadge label={r.status} tone={toneForStatus(r.status)} />
      </td>
      <td className="table-td mono">{formatMs(r.latencyMs)}</td>
      <td className="table-td mono text-canvas-muted">
        {r.inputTokens ?? "—"} / {r.outputTokens ?? "—"}
      </td>
      <td className="table-td mono">{r.freeTier ? "free" : formatUsd(r.costUsd)}</td>
      <td className="table-td text-xs text-canvas-muted">
        <Link to={`/requests?id=${r.id}`} className="hover:text-accent hover:underline">
          {r.routingExplanation?.selected?.displayName ?? "view trace →"}
        </Link>
        {r.fallbackCount > 0 && <span className="chip-warn ml-1">fb×{r.fallbackCount}</span>}
      </td>
    </tr>
  );
}
